/**
 * A photo keeps its colour space (1.180.0, #155).
 *
 * An iPhone shoots in Display P3, and so does what Lightroom exports from
 * it. Until 1.179 `prepareImage()` drew every photo into an sRGB canvas: the
 * browser converted the colours, and what lay outside sRGB was clipped.
 * From use, at the Lightroom export: "der Farbraum sollte nicht verändert
 * werden". So the canvas takes the colour space of the original, and the
 * result names it with a profile.
 *
 * **Only Display P3 is kept, and only where the file carries its profile.**
 * A canvas knows two spaces, sRGB and Display P3. A photo in Adobe RGB
 * would be converted into P3 by the browser, and tagging it with its own
 * profile would then be wrong; it stays sRGB as before. Recognised are the
 * primaries of the profile (`rXYZ`, `gXYZ`, `bXYZ`), not its name: Apple's
 * and Lightroom's profiles say "Display P3" in two encodings, and a third
 * would say it in a fourth.
 *
 * **The result carries a profile, whoever wrote it.** Chromium writes one
 * into the JPEG of a P3 canvas (measured: 536 bytes, and 472 for sRGB);
 * Safari is not measured. Where the encoder wrote none, the original's goes
 * in, which is the profile of the canvas by the rule above. A P3 JPEG
 * without a profile would be read as sRGB and look duller than the original.
 * And a browser that does not know a P3 canvas (`getContextAttributes()`
 * says `srgb`) gets no profile either: the pixels are sRGB then.
 *
 * Read from a JPEG's APP2 segments, or from a HEIF's `colr` box with
 * `prof` or `rICC`, which is what an iPhone writes. A `colr` of the `nclx`
 * kind names the space by number and carries no bytes to put into a JPEG;
 * such a photo stays sRGB.
 */
import { isJpeg, istHeif } from './exif'
import { APP2, behindLeadingApps, encode, join, payload, segment, segmentsOf, startsWith } from './jpegSegments'
import { heifMeta, propertiesOf } from './heif'
import { typeAt, type Box } from './mp4Boxes'

export type CanvasSpace = 'srgb' | 'display-p3'

const ICC_HEADER = 'ICC_PROFILE\0'
/** Bytes of profile per APP2 segment: 65533 payload minus the header and the two chunk numbers. */
const CHUNK = 65533 - ICC_HEADER.length - 2

/**
 * Display P3 as an ICC profile carries it: the primaries adapted to D50,
 * which is why red has a negative Z. Apple's profile and Lightroom's agree
 * to four places.
 */
const P3_PRIMARIES: Record<'rXYZ' | 'gXYZ' | 'bXYZ', [number, number, number]> = {
  rXYZ: [0.5151, 0.2412, -0.0011],
  gXYZ: [0.2920, 0.6922, 0.0419],
  bXYZ: [0.1571, 0.0666, 0.7841],
}
const TOLERANCE = 0.01

/** The ICC profile of a JPEG or HEIF, or null. */
export function iccOf(file: Uint8Array): Uint8Array | null {
  if (isJpeg(file)) return jpegIcc(file)
  if (istHeif(file)) return heifIcc(file)
  return null
}

function jpegIcc(jpeg: Uint8Array): Uint8Array | null {
  const chunks: { seq: number; data: Uint8Array }[] = []
  for (const s of segmentsOf(jpeg)) {
    if (s.marker !== APP2) continue
    const body = payload(jpeg, s)
    if (!startsWith(body, ICC_HEADER)) continue
    chunks.push({ seq: body[ICC_HEADER.length] ?? 0, data: body.subarray(ICC_HEADER.length + 2) })
  }
  if (chunks.length === 0) return null
  chunks.sort((a, b) => a.seq - b.seq)
  return join(chunks.map((c) => c.data))
}

/**
 * The profile of the primary picture, from its `colr` property, else the
 * first `colr` with profile bytes among all properties (an iPhone writes one
 * per picture item, all alike). Through `heif.ts` (1.187.0, review 2026-10b
 * C2), not by searching the first 4 MB byte by byte.
 */
function heifIcc(heif: Uint8Array): Uint8Array | null {
  const found = heifMeta(heif)
  if (found === null) return null
  const { view, meta, primary } = found
  const iprp = meta.find((b) => b.type === 'iprp')
  if (iprp === undefined) return null
  const props = propertiesOf(view, iprp)
  const withProfile = (box: Box): boolean => {
    if (box.type !== 'colr' || box.size < box.header + 4) return false
    const kind = typeAt(view, box.start + box.header)
    return kind === 'prof' || kind === 'rICC'
  }
  const own = primary === null ? undefined : props.get(primary)?.find(withProfile)
  const colr = own ?? [...props.values()].flat().find(withProfile)
  return colr === undefined ? null : heif.subarray(colr.start + colr.header + 4, colr.start + colr.size)
}

/** The three primaries of a profile, from its tag table; null for a profile without them. */
function primariesOf(icc: Uint8Array): Record<'rXYZ' | 'gXYZ' | 'bXYZ', [number, number, number]> | null {
  if (icc.length < 132) return null
  const view = new DataView(icc.buffer, icc.byteOffset, icc.byteLength)
  const count = view.getUint32(128)
  if (132 + 12 * count > icc.length) return null
  const found: Partial<Record<'rXYZ' | 'gXYZ' | 'bXYZ', [number, number, number]>> = {}
  for (let k = 0; k < count; k++) {
    const at = 132 + 12 * k
    const sig = String.fromCharCode(icc[at]!, icc[at + 1]!, icc[at + 2]!, icc[at + 3]!)
    if (sig !== 'rXYZ' && sig !== 'gXYZ' && sig !== 'bXYZ') continue
    const off = view.getUint32(at + 4)
    const len = view.getUint32(at + 8)
    if (len < 20 || off + len > icc.length) return null
    // s15Fixed16: a signed 32-bit number with sixteen binary places.
    found[sig] = [view.getInt32(off + 8) / 65536, view.getInt32(off + 12) / 65536, view.getInt32(off + 16) / 65536]
  }
  return found.rXYZ && found.gXYZ && found.bXYZ ? (found as Record<'rXYZ' | 'gXYZ' | 'bXYZ', [number, number, number]>) : null
}

/** Whether a profile is Display P3, by its primaries. */
export function isDisplayP3(icc: Uint8Array): boolean {
  const p = primariesOf(icc)
  if (p === null) return false
  return (['rXYZ', 'gXYZ', 'bXYZ'] as const).every((tag) =>
    p[tag].every((v, i) => Math.abs(v - P3_PRIMARIES[tag][i]!) <= TOLERANCE))
}

/** The canvas a photo with this profile is drawn into. */
export function canvasSpaceFor(icc: Uint8Array | null): CanvasSpace {
  return icc !== null && isDisplayP3(icc) ? 'display-p3' : 'srgb'
}

export function hasIcc(jpeg: Uint8Array): boolean {
  return jpegIcc(jpeg) !== null
}

/** The JPEG with the profile in APP2 segments behind JFIF and EXIF; one that has a profile is returned as it is. */
export function withIcc(jpeg: Uint8Array, icc: Uint8Array): Uint8Array<ArrayBuffer> {
  if (!isJpeg(jpeg) || hasIcc(jpeg)) return join([jpeg])
  const count = Math.max(1, Math.ceil(icc.length / CHUNK))
  const segments: Uint8Array[] = []
  for (let k = 0; k < count; k++) {
    const data = icc.subarray(k * CHUNK, (k + 1) * CHUNK)
    segments.push(segment(APP2, join([encode(ICC_HEADER), new Uint8Array([k + 1, count]), data])))
  }
  const at = behindLeadingApps(jpeg)
  return join([jpeg.subarray(0, at), ...segments, jpeg.subarray(at)])
}

/**
 * The colour space a 2D context really has. A browser that does not know
 * `colorSpace` gives an sRGB context and says so here; one without
 * `getContextAttributes()` is taken as sRGB too, because a profile on
 * sRGB pixels would be the wrong one.
 */
export function contextSpace(context: CanvasRenderingContext2D): CanvasSpace {
  const attrs = (context as { getContextAttributes?: () => { colorSpace?: string } }).getContextAttributes?.()
  return attrs?.colorSpace === 'display-p3' ? 'display-p3' : 'srgb'
}
